import { useCallback, useEffect, useState } from 'react';

/**
 * Maneja el cronometro de "Control de Tiempos Operativos" con INICIO EXPLICITO.
 *
 * IMPORTANTE (fix de bug #1): antes, el cronometro arrancaba solo con
 * Date.now() al abrir la pantalla, sin que el usuario hiciera nada. Si la
 * persona abandonaba la pantalla y volvia horas despues, el tiempo
 * transcurrido real no coincidia con lo que se guardaba (podian registrarse
 * 8h cuando la actividad real duro 30 min). Ahora el cronometro queda en
 * null/"sin iniciar" hasta que el usuario da clic en "Iniciar actividad" -
 * igual que en Toggl/Harvest. El boton "Iniciar actividad" tambien sirve
 * para reiniciar a mitad de camino (misma accion, mismo boton).
 *
 * IMPORTANTE (fix de bug #2): el Pausar/Despausar se retiro por completo.
 * Permitia dejar el cronometro "congelado" en pausa por horas sin ningun
 * registro de cuanto duro la pausa ni por que - un punto ciego para
 * auditoria, igual de riesgoso que el bug del auto-inicio. En su lugar
 * ahora existe "Cancelar Actividad" (ver ActivityForm.tsx /
 * CancelActivityModal.tsx): si el usuario necesita detenerse a medio
 * camino, cancela todo con confirmacion y empieza de cero despues.
 *
 * Persiste en localStorage por usuario para sobrevivir a refrescos de
 * pantalla (pero YA NO autoarranca si no hay nada guardado).
 */
export function useOperationTimer(userId: string) {
  const startKey = `lcc.timer.start.${userId}`;

  // null = "sin iniciar" (no arranca solo). Antes se seteaba automaticamente
  // con new Date().toISOString() al montar el componente - eso era el bug.
  const [startDateTime, setStartDateTime] = useState<string | null>(() => localStorage.getItem(startKey));
  const isRunning = startDateTime !== null;

  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!startDateTime) {
      setElapsedSeconds(0);
      return;
    }
    const tick = () => {
      const raw = Math.max(0, Math.round((Date.now() - new Date(startDateTime).getTime()) / 1000));
      setElapsedSeconds(raw);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startDateTime]);

  /** "Iniciar actividad": arranca el cronometro desde cero. Sirve tanto para
   * la primera vez como para reiniciar a mitad de camino (misma accion). */
  const start = useCallback(() => {
    const now = new Date().toISOString();
    setStartDateTime(now);
    localStorage.setItem(startKey, now);
  }, [startKey]);

  /** Deja el cronometro en "sin iniciar" (cero, detenido). Se llama tras un
   * Guardar exitoso o tras confirmar "Cancelar Actividad" - YA NO reinicia
   * automaticamente, hay que volver a darle "Iniciar actividad". */
  const stop = useCallback(() => {
    setStartDateTime(null);
    localStorage.removeItem(startKey);
  }, [startKey]);

  return { startDateTime, elapsedSeconds, isRunning, start, stop };
}
